import React, { ReactElement, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import PageTitleSubheader from '../../layout/subheader/PageTitleSubheader';
import { OutletContext } from '../../layout/Layout';
import {
	AlertVariant,
	Button,
	Modal,
	ModalVariant,
	Pagination,
	PaginationVariant,
	TextInput,
	Tooltip,
} from '@patternfly/react-core';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib/components/toast/ToastProvider';
import { ActivityLog, TActivityLog } from '../../api/activity-log/ActivityLog';
import Loader from '../../components/util/Loader';
import {
	SortByDirection,
	TableComposable,
	Tbody,
	Td,
	Th,
	Thead,
	ThProps,
	Tr,
} from '@patternfly/react-table';
import './ActivityLogs.scss';
import { timestampToMMDDYYYY } from '../../utilities';
import { faEye } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function ActivityLogs(): ReactElement {
	const columns = [
		'Log Date',
		'Activity Type',
		'User Account',
		'Log Activity',
		'Entity ID',
		'Entity Type',
		'Actions',
	];

	const { addToast } = useToast();
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [data, setData] = useState<TActivityLog[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
	const [pageNumber, setPageNumber] = React.useState<number>(1);
	const [perPage, setPerPage] = React.useState<number>(10);
	const [total, setTotal] = React.useState<number>();
	const [searchTerm, setSearchTerm] = React.useState<string>('');
	const [activeSortIndex, setActiveSortIndex] = React.useState<number>(0);
	const [activeSortDirection, setActiveSortDirection] = React.useState<SortByDirection>(
		SortByDirection.desc
	);
	const [currentLog, setCurrentLog] = useState<TActivityLog>();

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle="Activity Logs"
					pageDescription="View all activity logs."
					expanded={subNavExpanded}
					setExpanded={setSubNavExpanded}
				/>
			),
		});
		getData(1, 10, SortByDirection.desc, '');
	}, [setSubSide, subNavExpanded, setSubNavExpanded]);

	useEffect(() => {
		const handler = setTimeout(() => {
			if ((searchTerm.length > 2 || searchTerm.length == 0) && !isLoading) {
				setPageNumber(1);
				getData(1, perPage, activeSortDirection, searchTerm);
			}
		}, 500);

		return () => {
			clearTimeout(handler);
		};
	}, [searchTerm]);

	const getSortParams = (columnIndex: number): ThProps['sort'] => ({
		sortBy: {
			index: activeSortIndex,
			direction: activeSortDirection,
		},
		onSort: (_event, index, direction) => {
			setActiveSortIndex(index);
			setActiveSortDirection(direction);
			getData(pageNumber, perPage, direction, searchTerm);
		},
		columnIndex,
	});

	const getData = (
		pageNumber: number,
		perPage: number,
		sort: SortByDirection,
		searchTerm: string
	) => {
		setIsLoading(true);

		ActivityLog.GetAll(pageNumber, perPage, sort, searchTerm)
			.then((logs) => {
				setData(logs.data.items);
				setTotal(logs.data.total);
				setIsLoading(false);
			})
			.catch(() => {
				addToast(
					'An error occurred while trying to load Activity Logs. Please try again later.',
					AlertVariant.danger
				);
			});
	};

	const onSetPage = (
		_event: React.MouseEvent | React.KeyboardEvent | MouseEvent,
		newPage: number
	) => {
		setPageNumber(newPage);
		getData(newPage, perPage, activeSortDirection, searchTerm);
	};

	const onPerPageSelect = (
		_event: React.MouseEvent | React.KeyboardEvent | MouseEvent,
		newPerPage: number,
		newPage: number
	) => {
		setPerPage(newPerPage);
		setPageNumber(newPage);
		getData(newPage, newPerPage, activeSortDirection, searchTerm);
	};

	const handleClose = () => {
		setShowDetailModal(false);
	};

	const viewLog = (log: TActivityLog) => {
		setCurrentLog(log);
		setShowDetailModal(true);
	};

	return (
		<>
			<div className={'activity-log-container'}>
				{isLoading ? (
					<Loader />
				) : (
					<React.Fragment>
						<div className="actions">
							<TextInput
								type="text"
								aria-describedby="Search By Value"
								placeholder="Search By Value"
								value={searchTerm}
								onChange={(value) => setSearchTerm(value)}
							/>
						</div>

						<div className="value-table">
							<TableComposable>
								<Thead>
									<Tr>
										{columns.map((column, columnIndex) => {
											if (columnIndex === 0) {
												return (
													<Th
														key={columnIndex}
														sort={getSortParams(0)}
													>
														{column}
													</Th>
												);
											} else {
												return <Th key={columnIndex}>{column}</Th>;
											}
										})}
									</Tr>
								</Thead>
								<Tbody>
									{data.map((row, rowIndex) => (
										<Tr
											key={rowIndex}
											id={row.id.toString()}
										>
											<Td>{timestampToMMDDYYYY(row.created_at)}</Td>
											<Td>{row.activityType}</Td>
											<Td>{row.user}</Td>
											<Td>{row.message}</Td>
											<Td>{row.entity_id}</Td>
											<Td>{row.entity_type}</Td>
											<Td>
												<Tooltip
													content={<div>View Activity Log Record</div>}
												>
													<Button
														variant="plain"
														aria-label="Action"
														onClick={() => {
															viewLog(row);
														}}
													>
														<FontAwesomeIcon icon={faEye} />
													</Button>
												</Tooltip>
											</Td>
										</Tr>
									))}
								</Tbody>
							</TableComposable>
							<Pagination
								perPageComponent="button"
								itemCount={total}
								widgetId="table-pagination"
								perPage={perPage}
								page={pageNumber}
								variant={PaginationVariant.bottom}
								onSetPage={onSetPage}
								onPerPageSelect={onPerPageSelect}
							/>
						</div>
					</React.Fragment>
				)}
			</div>

			<Modal
				title={`Log Details`}
				variant={ModalVariant.medium}
				isOpen={showDetailModal}
				onClose={handleClose}
				actions={[
					<Button
						key="close"
						variant="secondary"
						onClick={handleClose}
					>
						Close
					</Button>,
				]}
				className="activity-log-modal"
			>
				{currentLog && (
					<>
						<div className="log-info-item">
							<div className="label">Log Date</div>
							<div className="log-value">
								{timestampToMMDDYYYY(currentLog.created_at)}
							</div>
						</div>
						<div className="log-info-item">
							<div className="label">Activity Type</div>
							<div className="log-value">{currentLog.activityType}</div>
						</div>
						<div className="log-info-item">
							<div className="label">User Account</div>
							<div className="log-value">{currentLog.user}</div>
						</div>
						<div className="log-info-item">
							<div className="label">Entity ID</div>
							<div className="log-value">{currentLog.entity_id}</div>
						</div>
						<div className="log-info-item">
							<div className="label">Entity Type</div>
							<div className="log-value">{currentLog.entity_id}</div>
						</div>
						<div className="log-info-item">
							<div className="label">IP Address</div>
							<div className="log-value">{currentLog.ipAddress}</div>
						</div>
						<div className="log-info-item">
							<div className="label">Log Activity</div>
							<div className="log-value">{currentLog.message}</div>
						</div>
						<div className="log-info-item">
							<div className="label">Context</div>
							<div className="log-value">
								{
									// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
									currentLog.context?.toString()
								}
							</div>
						</div>
					</>
				)}
			</Modal>
		</>
	);
}
